import { useEffect, useMemo, useState } from 'react';
import { Closet, ComboCloset } from '../components/Closet';
import { ArchiveDrawer } from '../components/ArchiveDrawer';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { DotRule } from '../components/DotRule';
import { fetchClosets, type Closet as ClosetData } from '../lib/queries';
import {
  fetchFeedCycle,
  currentCycleStart,
  nextReset,
  type FeedCycleRule,
} from '../lib/feed-cycle';
import './Feed.css';

const RESET_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric' });

// The two content types that share one wide cabinet, by drawer slug. Both get
// bespoke destination UI later; here they're just two labelled cupboards.
const COMBO_LEFT = 'sales-room';
const COMBO_RIGHT = 'monthly-zoom-qa';

export default function Feed() {
  const [closets, setClosets] = useState<ClosetData[]>([]);
  const [cycleRule, setCycleRule] = useState<FeedCycleRule>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        // The reset cadence drives the cycle floor: only this cycle's posts
        // front their closets. fetchFeedCycle never throws (falls back to off).
        const rule = await fetchFeedCycle(ctrl.signal);
        const cycleStart = currentCycleStart(rule);
        const data = await fetchClosets(ctrl.signal, cycleStart ? cycleStart.toISOString() : null);
        if (ctrl.signal.aborted) return;
        setCycleRule(rule);
        setClosets(data);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[feed] fetchClosets failed', err);
        setError(err?.message ?? 'Something broke loading the closet.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Split the wall: standalone tall cabinets keep drawer order; the combo
  // pair (Sales Room | Monthly Q&A) is pulled out and rendered as one wide
  // cabinet at the end.
  const { tall, comboLeft, comboRight } = useMemo(() => {
    const tall: ClosetData[] = [];
    let comboLeft: ClosetData | undefined;
    let comboRight: ClosetData | undefined;
    for (const c of closets) {
      if (c.drawer.slug === COMBO_LEFT) comboLeft = c;
      else if (c.drawer.slug === COMBO_RIGHT) comboRight = c;
      else tall.push(c);
    }
    return { tall, comboLeft, comboRight };
  }, [closets]);

  const cycleActive = !!cycleRule;
  const reset = nextReset(cycleRule);
  const openDate = reset ? RESET_FMT.format(reset) : null;

  return (
    <div className="feed">
      {/* halftone dot-field is now site-wide (global.css body) */}
      <header className="feed__header">
        <div className="container">
          <p className="kicker">The Closets</p>
          <h1 className="feed__headline">What's in the room?</h1>
          {cycleActive && reset && (
            <p className="feed__cycle">
              Closets Reset {RESET_FMT.format(reset)}
            </p>
          )}
          <DotRule className="feed__rule" />
        </div>
      </header>

      <div className="container">
        {loading ? (
          <div className="feed__wall" aria-busy="true" aria-label="Loading the closet">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="feed__closet-skel">
                <Skeleton variant="card" aspectRatio={3 / 4.2} />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState eyebrow="Error" headline="We couldn't open the closet." body={error} />
        ) : closets.length === 0 ? (
          <EmptyState
            eyebrow="Nothing here yet"
            headline="The room is quiet, for now."
            body="The first pieces are publishing this week."
          />
        ) : (
          <>
            <div className="feed__wall">
              {tall.map((c) => (
                <Closet key={c.drawer.id} drawer={c.drawer} post={c.post} cycleActive={cycleActive} openDate={openDate} />
              ))}
            </div>

            {comboLeft && comboRight && (
              <div className="feed__combo">
                <ComboCloset left={comboLeft} right={comboRight} cycleActive={cycleActive} openDate={openDate} />
              </div>
            )}

            <div className="feed__archive">
              <ArchiveDrawer />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
